import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  User,
  CreditCard,
  Bell,
  Shield,
  Check,
  Loader2,
  Upload,
  Trash2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import DashboardLayout from '../components/DashboardLayout';
import BillingTab from '../components/billing/BillingTab';
import { useLocation } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, updateUser, logout } = useAuth();
  const location = useLocation();
  // Open the tab a link asks for: /settings?tab=billing (also Stripe's return URL) or /settings/billing
  const initialTab = new URLSearchParams(location.search).get('tab')
    || (location.pathname.endsWith('/billing') ? 'billing' : 'profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  const [profileForm, setProfileForm] = useState({ name: '', email: '' });

  const [notifications, setNotifications] = useState({
    email_approvals: true,
    email_comments: true,
    email_published: true,
    email_digest: false,
    push_enabled: false,
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new_pw: false, confirm: false });

  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const notifsRes = await api.get('/auth/notifications');
      if (notifsRes.data) {
        setNotifications(notifsRes.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', email: user.email || '' });
    }
  }, [user]);

  // ─── Profile ──────────────────────────────────

  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/auth/profile', { name: profileForm.name.trim() });
      if (res.data.user) {
        updateUser(res.data.user);
      }
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, GIF, or WebP images are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File must be under 2MB');
      return;
    }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/auth/upload-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.picture) {
        updateUser({ picture: res.data.picture });
      }
      toast.success('Profile picture updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploadingPicture(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ─── Notifications ──────────────────────────────

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      const res = await api.put('/auth/notifications', notifications);
      if (res.data.notification_preferences) {
        setNotifications(res.data.notification_preferences);
      }
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  // ─── Password ──────────────────────────────

  const handleChangePassword = async () => {
    const { current_password, new_password, confirm_password } = passwordForm;
    if (!current_password || !new_password || !confirm_password) {
      toast.error('All password fields are required');
      return;
    }
    if (new_password !== confirm_password) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);
    try {
      await api.put('/auth/password', { current_password, new_password, confirm_password });
      toast.success('Password updated. Please log in again.');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => logout(), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  // ─── Delete Account ──────────────────────────────

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    setDeletingAccount(true);
    try {
      await api.delete('/auth/account', { data: { confirmation: 'DELETE' } });
      toast.success('Account deleted');
      setTimeout(() => logout(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  // ─── Render ──────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  const passwordMismatch =
    passwordForm.new_password &&
    passwordForm.confirm_password &&
    passwordForm.new_password !== passwordForm.confirm_password;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Settings className="h-6 w-6 text-emerald-500" />
            Settings
          </h1>
          <p className="text-slate-600 mt-1">Manage your account and preferences</p>
        </div>

        <Tabs defaultValue={initialTab} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="profile"><User className="h-4 w-4 mr-2" />Profile</TabsTrigger>
            <TabsTrigger value="billing"><CreditCard className="h-4 w-4 mr-2" />Billing</TabsTrigger>
            <TabsTrigger value="notifications"><Bell className="h-4 w-4 mr-2" />Notifications</TabsTrigger>
            <TabsTrigger value="security"><Shield className="h-4 w-4 mr-2" />Security</TabsTrigger>
          </TabsList>

          {/* ─── Profile Tab ─── */}
          <TabsContent value="profile">
            <div className="grid gap-6">
              <Card className="border-slate-100">
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>Update your personal details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-6">
                    <Avatar className="h-20 w-20">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-2xl">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePictureUpload}
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="hidden"
                        data-testid="profile-picture-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPicture}
                        data-testid="change-photo-btn"
                      >
                        {uploadingPicture ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                        ) : (
                          <><Upload className="h-4 w-4 mr-2" />Change Photo</>
                        )}
                      </Button>
                      <p className="text-xs text-slate-500 mt-1">JPG, PNG or GIF. Max 2MB.</p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        data-testid="profile-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={profileForm.email}
                        disabled
                        data-testid="profile-email-input"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="save-profile-btn"
                    >
                      {saving ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ─── Billing Tab ─── */}
          <TabsContent value="billing">
            <BillingTab />
          </TabsContent>

          {/* ─── Notifications Tab ─── */}
          <TabsContent value="notifications">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Choose how you want to be notified</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-medium text-slate-900 mb-4">Email Notifications</h4>
                  <div className="space-y-4">
                    {[
                      { key: 'email_approvals', title: 'Approval Requests', desc: 'Get notified when posts need approval' },
                      { key: 'email_comments', title: 'Comments', desc: 'Get notified about new comments on your posts' },
                      { key: 'email_published', title: 'Published Posts', desc: 'Get notified when posts are published' },
                      { key: 'email_digest', title: 'Weekly Digest', desc: 'Receive a weekly summary of your activity' },
                    ].map((item, idx) => (
                      <React.Fragment key={item.key}>
                        {idx > 0 && <Separator />}
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-700">{item.title}</p>
                            <p className="text-sm text-slate-500">{item.desc}</p>
                          </div>
                          <Switch
                            checked={notifications[item.key]}
                            onCheckedChange={(checked) => setNotifications({ ...notifications, [item.key]: checked })}
                            data-testid={`notif-${item.key}-switch`}
                          />
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-slate-900 mb-4">Push Notifications</h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">Enable Push Notifications</p>
                      <p className="text-sm text-slate-500">Receive push notifications in your browser</p>
                    </div>
                    <Switch
                      checked={notifications.push_enabled}
                      onCheckedChange={(checked) => setNotifications({ ...notifications, push_enabled: checked })}
                      data-testid="notif-push-switch"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    onClick={handleSaveNotifications}
                    disabled={savingNotifications}
                    data-testid="save-notifications-btn"
                  >
                    {savingNotifications ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                      'Save Preferences'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Security Tab ─── */}
          <TabsContent value="security">
            <div className="grid gap-6">
              <Card className="border-slate-100">
                <CardHeader>
                  <CardTitle>Password</CardTitle>
                  <CardDescription>Change your password</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Current Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.current ? 'text' : 'password'}
                        placeholder="Enter current password"
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.new_pw ? 'text' : 'password'}
                        placeholder="At least 6 characters"
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPasswords({ ...showPasswords, new_pw: !showPasswords.new_pw })}
                      >
                        {showPasswords.new_pw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        placeholder="Re-enter new password"
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                        className={passwordMismatch ? 'border-red-400 focus:ring-red-400' : ''}
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordMismatch && (
                      <p className="text-sm text-red-500" data-testid="password-mismatch-error">
                        Passwords do not match
                      </p>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <Button
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      onClick={handleChangePassword}
                      disabled={savingPassword || !!passwordMismatch}
                      data-testid="update-password-btn"
                    >
                      {savingPassword ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating...</>
                      ) : (
                        'Update Password'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-100">
                <CardHeader>
                  <CardTitle>Two-Factor Authentication</CardTitle>
                  <CardDescription>Add an extra layer of security to your account</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">2FA Status</p>
                      <p className="text-sm text-slate-500">Two-factor authentication is currently disabled</p>
                    </div>
                    <Button variant="outline">Enable 2FA</Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-100">
                <CardHeader>
                  <CardTitle>Onboarding Tour</CardTitle>
                  <CardDescription>Replay the interactive walkthrough of StelliSocial</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">Interactive Tour</p>
                      <p className="text-sm text-slate-500">Restart the guided tour that walks you through all the main features</p>
                    </div>
                    <Button
                      variant="outline"
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      onClick={() => {
                        localStorage.removeItem('stellisocial_tour_completed');
                        window.location.href = '/dashboard';
                      }}
                      data-testid="restart-tour-btn"
                    >
                      Restart Tour
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-100 border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-600">Danger Zone</CardTitle>
                  <CardDescription>Irreversible actions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="font-medium text-slate-700">Delete Account</p>
                    <p className="text-sm text-slate-500 mb-3">
                      Permanently delete your account and all data. This cannot be undone.
                    </p>
                    <div className="flex items-center gap-3">
                      <Input
                        placeholder='Type "DELETE" to confirm'
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        className="max-w-xs"
                        data-testid="delete-confirmation-input"
                      />
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount || deleteConfirmation !== 'DELETE'}
                        data-testid="delete-account-btn"
                      >
                        {deletingAccount ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                        ) : (
                          <><Trash2 className="h-4 w-4 mr-2" />Delete Account</>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
